import { OverflowMenuHorizontal, WarningAlt } from '@carbon/icons-react';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Body,
  Link,
  Menu,
  MenuItem,
  MenuItems,
  StaticBadge,
  Tooltip,
} from 'vet-bones/design-system/components';
import { useIsMounted } from 'vet-bones/utils/hooks';

import { useAppDispatch } from 'src/ui/app/hooks';
import {
  LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU,
  LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM,
  LAUNCHPAD_ENDPOINT_TABLE_ROW_NAME,
  LAUNCHPAD_ENDPOINT_TABLE_ROW_SSO_REQUIRED_WARNING,
} from 'src/ui/constants/testIds';
import { SduMenuActions } from 'src/ui/containers/endpoints/SduMenuActions';
import { getEndpointAuthType } from 'src/ui/containers/endpoints/utils/getAuthType';
import { getEndpointUrl } from 'src/ui/containers/endpoints/utils/getEndpointUrl';
import { useDownloadLogs } from 'src/ui/containers/endpoints/utils/useDownloadLogs';
import { useHasReadLogsPermissions } from 'src/ui/containers/endpoints/utils/useHasReadLogsPermissions';
import { useIsSduSupported } from 'src/ui/containers/endpoints/utils/useIsSduSupported';
import { editConnection } from 'src/ui/features/connection';
import { DialogType, openedDialog } from 'src/ui/features/ui';
import { Connection } from 'src/ui/graph/types';
import { useEndpointVersionGate } from 'src/ui/hooks/endpoint';
import { useValidateConnection } from 'src/ui/hooks/validateConnection';
import * as copy from 'src/ui/templates/copy';
import { buildRoute } from 'src/ui/utils/buildRoute';
import { isSsoConnectionRequired } from 'src/ui/utils/features';

export interface LaunchpadEndpointRowProps {
  connection: Connection;
}

export const LaunchpadEndpointRow: React.VFC<{ connection: Connection }> = ({
  connection,
}) => {
  const isMounted = useIsMounted();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const validateConnection = useValidateConnection();

  const isDownloadLogsSupported = useEndpointVersionGate(connection, '11.1.0');
  const isDownloadVoiceboxReportSupported = useEndpointVersionGate(
    connection,
    '10.0.0'
  );
  const isSduSupported = useIsSduSupported(connection);
  const canReadLogs = useHasReadLogsPermissions(
    connection,
    isDownloadLogsSupported ||
      isDownloadVoiceboxReportSupported ||
      isSduSupported
  );

  const [isDownloadingLogs, setIsDownloadingLogs] = React.useState(false);
  const downloadLogs = useDownloadLogs(connection);

  const handleClick = () => {
    history.push(buildRoute('', connection.index));
  };

  const handleMenuInteraction = (isOpening: boolean) => {
    if (isOpening) {
      validateConnection(connection);
    }
  };

  const handleEditEndpoint = () => {
    dispatch(editConnection(connection.index));
    dispatch(openedDialog({ type: DialogType.EDIT_ENDPOINT }));
  };

  const handleDiagnostic = () => {
    history.push(buildRoute('', connection.index, 'diagnostic'));
  };

  const handleDownloadLogs = async () => {
    setIsDownloadingLogs(true);
    await downloadLogs();
    if (isMounted.current) {
      setIsDownloadingLogs(false);
    }
  };

  const handleDownloadVoiceboxReport = () => {
    dispatch(
      openedDialog({
        type: DialogType.VOICEBOX_RESPONSE_REPORT,
        data: { connection },
      })
    );
  };

  const handleDeleteEndpoint = () => {
    dispatch(editConnection(connection.index));
    dispatch(openedDialog({ type: DialogType.DELETE_ENDPOINT }));
  };

  const authType = getEndpointAuthType(connection);

  const actions = (
    <>
      <MenuItem
        leftIcon={null}
        onClick={handleEditEndpoint}
        testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM('edit-endpoint')}
        text={copy.components.endpoints.endpointTable.actions.editEndpoint}
      />
      <MenuItem
        leftIcon={null}
        onClick={handleDiagnostic}
        testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
          'view-diagnostics'
        )}
        text={copy.components.endpoints.endpointTable.actions.viewDiagnostics}
      />
      {isDownloadLogsSupported ? (
        <Tooltip
          description={copy.components.endpoints.endpointTable.actions.downloadLogsPermissionsTooltip(
            connection.name
          )}
          disabled={canReadLogs}
        >
          <MenuItem
            disabled={!canReadLogs || isDownloadingLogs}
            leftIcon={null}
            onClick={handleDownloadLogs}
            testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
              'download-logs'
            )}
            text={copy.components.endpoints.endpointTable.actions.downloadLogs}
          />
        </Tooltip>
      ) : null}
      {isDownloadVoiceboxReportSupported ? (
        <Tooltip
          description={copy.components.endpoints.endpointTable.actions.downloadVoiceboxReportPermissionsTooltip(
            connection.name
          )}
          disabled={canReadLogs}
        >
          <MenuItem
            disabled={!canReadLogs}
            leftIcon={null}
            onClick={handleDownloadVoiceboxReport}
            testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
              'download-voicebox-report'
            )}
            text={
              copy.components.endpoints.endpointTable.actions
                .downloadVoiceboxReport
            }
          />
        </Tooltip>
      ) : null}
      {isSduSupported ? (
        <SduMenuActions
          canReadLogs={canReadLogs}
          connection={connection}
          downloadTestId={LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
            'download-sdu'
          )}
        />
      ) : null}
      <MenuItem
        leftIcon={null}
        onClick={handleDeleteEndpoint}
        testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
          'delete-endpoint'
        )}
        text={copy.components.endpoints.endpointTable.actions.deleteEndpoint}
      />
    </>
  );

  let authLabel;
  if (authType === 'sso') {
    authLabel = copy.components.endpoints.endpointTable.row.auth.sso;
  } else if (authType === 'browser-auth') {
    authLabel = copy.components.endpoints.endpointTable.row.auth.browser;
  } else {
    authLabel = copy.components.endpoints.endpointTable.row.auth.username(
      connection.username || ''
    );
  }

  return (
    <>
      <td className="organization-table-row-endpoint-name">
        <Link
          onClick={handleClick}
          size="md"
          testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_NAME(
            connection.id,
            connection.index
          )}
        >
          {connection.name}
        </Link>
      </td>
      <td className="organization-table-row-endpoint-url">
        <Body size="md">{getEndpointUrl(connection)}</Body>
      </td>
      <td className="organization-table-row-endpoint-auth">
        <Body size="md">{authLabel}</Body>
        {isSsoConnectionRequired() && authType === 'username' ? (
          <Tooltip
            description={
              copy.components.endpoints.endpointTable.row.auth
                .ssoRequiredWarning.tooltip
            }
          >
            <StaticBadge
              leftIcon={WarningAlt}
              size="md"
              testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_SSO_REQUIRED_WARNING(
                connection.id,
                connection.index
              )}
              type="warning"
              variant="muted"
            >
              {
                copy.components.endpoints.endpointTable.row.auth
                  .ssoRequiredWarning.label
              }
            </StaticBadge>
          </Tooltip>
        ) : null}
      </td>
      <td className="data-table-row-actions">
        <Menu
          leftIcon={OverflowMenuHorizontal}
          menu={
            <MenuItems className="data-table-menu-items">{actions}</MenuItems>
          }
          onInteraction={handleMenuInteraction}
          size="sm"
          testId={LAUNCHPAD_ENDPOINT_TABLE_ROW_ACTIONS_MENU(
            connection.id,
            connection.index
          )}
        />
      </td>
    </>
  );
};
